<script setup lang="ts">
import { onMounted, watch, computed } from "vue";
import { useSearchStore } from "@/js/stores/useSearchStore";
import Pagination from "@/js/components/pieces/Pagination.vue";
import { trackAlgoliaSearchClick } from "@/js/utils/algoliaInsights";

const searchStore = useSearchStore();
const RESULTS_PER_PAGE = 12;
const hasFetchedInitially = { value: false };

const isAdvancedVideoSearch = () =>
    typeof window !== "undefined" &&
    window.location.pathname.includes("/video-library/advanced-search");

const isSearchResultsRoute = () =>
    typeof window !== "undefined" &&
    (window.location.pathname.includes("/search") || isAdvancedVideoSearch());

// Results range numbers for display
const pageFirstResultNumber = computed(() =>
    searchStore.total > 0 ? (searchStore.page - 1) * RESULTS_PER_PAGE + 1 : 0,
);

const pageLastResultNumber = computed(() =>
    Math.min(searchStore.total, searchStore.page * RESULTS_PER_PAGE),
);

const prevLink = computed(() =>
    searchStore.page > 1 ? `/search?page=${searchStore.page - 1}` : "",
);
const nextLink = computed(() =>
    searchStore.page < searchStore.totalPages
        ? `/search?page=${searchStore.page + 1}`
        : "",
);

const isEmptyState = computed(() => {
    return (
        !searchStore.query &&
        !searchStore.selectedTopic &&
        !searchStore.selectedResourceType &&
        !searchStore.isLoading &&
        searchStore.results.length === 0
    );
});

const isNoResultsState = computed(() => {
    return (
        (searchStore.query ||
            searchStore.selectedTopic ||
            searchStore.selectedResourceType) &&
        !searchStore.isLoading &&
        searchStore.results.length === 0
    );
});

const noResultsMessage = computed(() => {
    const query = searchStore.query?.trim();
    const topic = searchStore.selectedTopic?.trim();
    const type = searchStore.selectedResourceType?.trim();

    const filters: string[] = [];
    if (topic) filters.push(`Topic: <b>${topic}</b>`);
    if (type) filters.push(`Resource Type: <b>${type}</b>`);

    let message = "Your ";

    if (query) {
        message += `search for <b>"${query}"</b>`;
        if (filters.length) {
            message += ` with ${filters.join(" and ")}`;
        }
    } else if (filters.length) {
        message += `filters ${filters.join(" and ")}`;
    }

    message += " returned no results.";

    return message;
});

const noResultsActionMessage = computed(() => {
    const query = searchStore.query?.trim();
    const hasFilters =
        searchStore.selectedTopic || searchStore.selectedResourceType;

    if (query && hasFilters) return "Try adjusting your search or filters.";
    if (query) return "Try a different search term.";
    if (hasFilters) return "Try adjusting your filters.";

    return "";
});

const trackClick = (result: any, position: number) => {
    trackAlgoliaSearchClick(result, position);
};

// Handle tag removal
const clearFilter = (type: "query" | "topic" | "resourceType") => {
    hasFetchedInitially.value = true;
    searchStore.page = 1;

    if (type === "query") searchStore.query = "";
    if (type === "topic") searchStore.selectedTopic = "";
    if (type === "resourceType") searchStore.selectedResourceType = "";

    searchStore.updateURL({
        q: searchStore.query,
        topic: searchStore.selectedTopic,
        resourceType: searchStore.selectedResourceType,
        page: searchStore.page,
    });

    searchStore.fetchSearchResults();
};

// Handle pagination click
const paginate = (targetPage: number) => {
    if (targetPage >= 1 && targetPage <= searchStore.totalPages) {
        hasFetchedInitially.value = true;
        searchStore.page = targetPage;
        searchStore.fetchSearchResults();
        searchStore.updateURL({
            q: searchStore.query,
            topic: searchStore.selectedTopic,
            resourceType: searchStore.selectedResourceType,
            page: targetPage,
        });
    }
};

onMounted(() => {
    const params = new URLSearchParams(window.location.search);
    searchStore.query = params.get("q") || "";
    searchStore.selectedTopic = params.get("topic") || "";
    searchStore.selectedResourceType =
        params.get("resourceType") || (isAdvancedVideoSearch() ? "Video" : "");
    searchStore.page = parseInt(params.get("page") || "1");

    hasFetchedInitially.value = true;

    const hasQueryOrFilters =
        searchStore.query.trim() ||
        searchStore.selectedTopic ||
        searchStore.selectedResourceType;

    if (isSearchResultsRoute() && hasQueryOrFilters) {
        searchStore.fetchSearchResults();
    }
});

watch(
    () => [
        searchStore.query,
        searchStore.selectedTopic,
        searchStore.selectedResourceType,
        searchStore.page,
    ],
    ([newQuery, newTopic, newType], [oldQuery, oldTopic, oldType]) => {
        if (hasFetchedInitially.value) {
            hasFetchedInitially.value = false;
            return;
        }

        if (
            newQuery !== oldQuery ||
            newTopic !== oldTopic ||
            newType !== oldType
        ) {
            searchStore.fetchSearchResults();
        }
    },
);

watch(
    () => [searchStore.selectedTopic, searchStore.selectedResourceType],
    () => {
        if (isSearchResultsRoute()) {
            searchStore.page = 1;
            searchStore.updateURL({
                q: searchStore.query,
                topic: searchStore.selectedTopic,
                resourceType: searchStore.selectedResourceType,
                page: 1,
            });
            searchStore.fetchSearchResults();
        }
    },
);

const getPreferredHtml = (result: any): string => {
    if (result.teaser) return result.teaser;
    if (result.autoDescription) return result.autoDescription;

    if (result.copy && result.title) {
        const pattern = new RegExp(
            `<p><strong>${result.title}</strong> -\\s*`,
            "i",
        );
        return result.copy.replace(pattern, "<p>");
    }

    return "";
};
</script>

<template>
    <section>
        <!-- Selected Filters -->
        <div
            class="py-6 bg-blue-6 text-black overflow-hidden"
            v-if="
                searchStore.selectedTopic ||
                searchStore.selectedResourceType ||
                searchStore.query
            "
        >
            <div
                class="wrapper flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
            >
                <span class="heading-01 text-2xl">You searched for:</span>
                <div class="flex flex-wrap gap-2">
                    <span
                        v-if="searchStore.query"
                        class="px-3 py-1 bg-blue-11 text-white rounded-md text-sm font-bold inline-flex items-center gap-2"
                    >
                        Keyword: "{{ searchStore.query.toLowerCase() }}"
                        <button
                            @click="clearFilter('query')"
                            class="text-white text-xs"
                        >
                            ✕
                        </button>
                    </span>
                    <span
                        v-if="searchStore.selectedTopic"
                        class="px-3 py-1 bg-blue-11 text-white rounded-md text-sm font-bold inline-flex items-center gap-2"
                    >
                        Topic: "{{ searchStore.selectedTopic }}"
                        <button
                            @click="clearFilter('topic')"
                            class="text-white text-xs"
                        >
                            ✕
                        </button>
                    </span>
                    <span
                        v-if="searchStore.selectedResourceType"
                        class="px-3 py-1 bg-blue-11 text-white rounded-md text-sm font-bold inline-flex items-center gap-2"
                    >
                        Resource Type: "{{ searchStore.selectedResourceType }}"
                        <button
                            @click="clearFilter('resourceType')"
                            class="text-white text-xs"
                        >
                            ✕
                        </button>
                    </span>
                </div>

                <div class="richtext md:ml-auto" v-if="searchStore.total">
                    <p>
                        Showing
                        <b
                            >{{ pageFirstResultNumber }} -
                            {{ pageLastResultNumber }}</b
                        >
                        of {{ searchStore.total }} results
                    </p>
                </div>
            </div>
        </div>

        <!-- Results List -->
        <div class="wrapper py-8 md:py-12">
            <ul class="flex flex-col" v-if="searchStore.results.length">
                <li
                    v-for="(result, index) in searchStore.results"
                    :key="result.objectID"
                    :class="[
                        'border-b border-gray-4',
                        result.sectionHandle === 'glossary'
                            ? ''
                            : ' hover:bg-blue-light-3',
                    ]"
                >
                    <!-- Glossary Entry -->
                    <div
                        v-if="result.sectionHandle === 'glossary'"
                        class="bg-blue-50 border-l-4 border-r-4 border-blue-11 text-blue-11 px-6 py-4 my-4 rounded-md"
                    >
                        <p
                            class="text-sm uppercase font-bold tracking-wide text-blue-11 mb-1"
                        >
                            Definition
                        </p>
                        <p class="font-semibold text-lg mb-2 text-black">
                            <span
                                v-if="result.displayTitle"
                                v-html="result.displayTitle"
                            ></span>
                            <template v-else>{{ result.title }}</template>
                        </p>
                        <div
                            v-html="getPreferredHtml(result)"
                            class="text-base text-black leading-relaxed prose prose-sm max-w-none"
                        ></div>
                    </div>

                    <!-- Author Result -->
                    <a
                        v-else-if="result.type === 'author' || result.isAuthor"
                        class="flex flex-col md:flex-row gap-6 md:gap-10 animation-zoom px-4 py-8 md:p-10 items-center"
                        :href="result.link"
                        @click="
                            trackClick(
                                result,
                                (searchStore.page - 1) * RESULTS_PER_PAGE +
                                    index +
                                    1,
                            )
                        "
                    >
                        <div
                            class="w-32 h-32 md:w-40 md:h-40 rounded-full bg-blue-11 flex-shrink-0 bg-cover bg-center overflow-hidden shadow-md"
                            :style="
                                result.photoUrl
                                    ? `background-image: url('${result.photoUrl}')`
                                    : ''
                            "
                        >
                            <svg
                                v-if="!result.photoUrl"
                                class="w-full h-full text-white p-4"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                />
                            </svg>
                        </div>
                        <div class="w-full md:flex-1 text-center md:text-left">
                            <span
                                class="inline-block px-3 py-1 mb-2 text-xs font-bold uppercase tracking-wider text-white bg-blue-default rounded-full"
                                >Contributor</span
                            >
                            <h2
                                class="text-2xl md:text-3xl font-bold text-black mb-3"
                            >
                                {{ result.fullName }}
                            </h2>
                            <div
                                v-if="result.copy"
                                class="text-gray-600 max-w-2xl mx-auto md:mx-0 line-clamp-3"
                            >
                                {{ result.copy }}
                            </div>
                        </div>
                    </a>

                    <a
                        v-else
                        class="flex flex-col md:flex-row gap-10 animation-zoom px-4 py-8 md:p-10"
                        :href="result.link"
                        @click="
                            trackClick(
                                result,
                                (searchStore.page - 1) * RESULTS_PER_PAGE +
                                    index +
                                    1,
                            )
                        "
                    >
                        <img
                            v-if="result.mainImageUrl?.length"
                            :src="result.mainImageUrl"
                            :alt="result.mainImageAltText"
                            class="w-full md:w-1/3 lg:w-1/5 h-auto object-contain flex-shrink-0"
                        />
                        <div class="w-full md:w-2/3 lg:w-4/5">
                            <h2 class="text-2xl font-semibold underline">
                                <span
                                    v-if="result.displayTitle"
                                    v-html="result.displayTitle"
                                ></span>
                                <template v-else>{{ result.title }}</template>
                            </h2>
                            <div
                                class="richtext richtext--base md:richtext--lg"
                            >
                                <div
                                    v-if="getPreferredHtml(result)"
                                    v-html="getPreferredHtml(result)"
                                    class="text-sm text-gray-600 cursor-text"
                                ></div>
                                <div
                                    class="flex flex-wrap gap-2 mt-1"
                                    v-if="result.relatedResourceTypes?.length"
                                >
                                    <div
                                        v-for="type in result.relatedResourceTypes"
                                        :key="type"
                                        class="flex-initial px-2 py-1 inline-flex items-center justify-start gap-2 rounded-md uppercase font-bold bg-orange-5 text-white cursor-text !mb-0"
                                    >
                                        <small class="tag">{{ type }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </a>
                </li>
            </ul>

            <!-- No Results -->
            <div v-else-if="isNoResultsState" class="wrapper py-10" v-cloak>
                <div
                    class="inner grid gap-3 p-8 shadow-popover rounded-lg text-center"
                >
                    <h2 class="heading-05">No results found</h2>
                    <div class="richtext richtext--lg">
                        <p v-html="noResultsMessage"></p>
                        <p class="mt-4">{{ noResultsActionMessage }}</p>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="isEmptyState" class="wrapper py-10">
                <div
                    class="inner grid gap-3 p-8 shadow-popover rounded-lg text-center"
                >
                    <h2 class="heading-05">Please enter a search term.</h2>
                    <div class="richtext richtext--lg">
                        <p>
                            Enter a search term or select a filter to find
                            relevant resources and information.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div
            class="w-full flex justify-center"
            v-if="searchStore.totalPages > 1"
        >
            <Pagination
                :currentPage="searchStore.page"
                :totalPages="searchStore.totalPages"
                :perPage="RESULTS_PER_PAGE"
                :total="searchStore.total"
                :count="searchStore.results.length"
                :previousLink="prevLink"
                :nextLink="nextLink"
                @paginate="paginate"
            />
        </div>
    </section>
</template>
